import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { PriceTier, serializePriceTier } from '../../../models/PriceTier';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import {
    bulkChangePriceByLineItemKeysInList,
    BulkChangePriceByLineItemKeysRequest
} from './bulkChangePriceByLineItemKeysInList';
import { bulkChangePriceByFilterInList } from './bulkChangePriceByFilterInList';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { invalidateSelectionListSummary } from './useSelectionListSummary';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import { BulkAddProductsToSelectionListResponse } from '../../store/api';
import { invalidateSelectionListGroupedItems } from './useSelectionListGroupedItems';
import { BulkChangePriceByFilterRequest } from '../shopping-cart/bulkChangePriceByFilterInCart';
import { bulkUpdateToList } from './bulkUpdateToList';
import { convertLineItemKeysToDtoFromProductSelection } from '../../shared/utils/convertLineItemKey';

type GetRequestParams = {
    selectionListId: string;
    productSelection: ProductSelection;
    filterRequest: string;
    performChecks: boolean;
    priceTier: PriceTier;
};

export const buildChangePriceCallback = (
    { selectionListId, productSelection, filterRequest, performChecks, priceTier }: GetRequestParams,
    t: TFunction
): (() => Promise<BulkAddProductsToSelectionListResponse>) => {
    return () => {
        if (isIncludeProductSelection(productSelection)) {
            const body: BulkChangePriceByLineItemKeysRequest = {
                lineItemKeys: convertLineItemKeysToDtoFromProductSelection(productSelection),
                targetPrice: serializePriceTier(priceTier)!
            };
            return bulkChangePriceByLineItemKeysInList({
                selectionListId,
                body,
                params: {
                    performChecks
                }
            });
        } else if (isExcludeProductSelection(productSelection)) {
            const body: BulkChangePriceByFilterRequest = {
                filterRequest: deserializeFilter(filterRequest, {
                    includeKey: 'include',
                    excludeKey: 'exclude'
                }),
                excludedLineItemKeys: productSelection.excluded,
                targetPrice: serializePriceTier(priceTier)!
            };
            return bulkChangePriceByFilterInList({
                selectionListId,
                body,
                params: {
                    performChecks
                }
            });
        } else {
            throw new Error(t('notifications.buildAddProductsCallback.error'));
        }
    };
};

type Props = {
    filter: string;
};

export const useChangePriceInList = ({ filter }: Props) => {
    const { t } = useTranslation();
    const dispatchRedux = useDispatch();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        changePriceBulk: (
            priceTier: PriceTier,
            listId: string,
            performChecks: boolean,
            productSelection: ProductSelection
        ) => {
            const apiCallback = buildChangePriceCallback(
                {
                    selectionListId: listId,
                    productSelection,
                    filterRequest: filter,
                    performChecks,
                    priceTier
                },
                t
            );

            return bulkUpdateToList({
                performChecks,
                dispatch: dispatchRedux,
                setLoading,
                notificationMessage: t('notifications.changePriceBulk.message'),
                apiCallback,
                t,
                onSuccess: () => {
                    invalidateSelectionListAggregations(listId).then();
                    invalidateSelectionListSummary(listId).then();
                    invalidateSelectionListGroupedItems(listId).then();
                    invalidateSelectionListProducts(listId).then();
                }
            });
        }
    };
};
